import React, { useState, useEffect } from 'react';
import { Brain, Calculator, MapPin, BookOpen, Send, CheckCircle, GraduationCap, CreditCard, Download, ArrowLeft, ThumbsUp, ThumbsDown, Bug, AlertTriangle, Calendar, BarChart3 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { loadRazorpayScript, createRazorpayOrder, verifyPayment, validatePromoCode, debugRazorpayConnection, RAZORPAY_KEY_ID, type RazorpayResponse } from '../lib/razorpay';
import { personalizedListSubmissionService } from '../lib/database';
import { personalizedCollegeService, type BranchRecommendation } from '../lib/personalizedCollegeService';
import { generatePDF, generatePDFBase64 } from '../lib/pdfGenerator';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';
import SEOHead from '../components/SEOHead';

const PersonalizedList = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    exam: '',
    rank: '',
    category: '',
    states: [] as string[],
    stream: '',
    finalAmount: 299,
    promoCode: ''
  });
  const [loading, setLoading] = useState(false);
  const [generatedList, setGeneratedList] = useState<BranchRecommendation[]>([]);
  const [showResults, setShowResults] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [promoApplied, setPromoApplied] = useState(false);
  const [discount, setDiscount] = useState(0);
  const [debugMode, setDebugMode] = useState(false);

  const exams = ['NEET'];
  const categories = ['General', 'OBC', 'SC', 'ST', 'EWS'];
  const streams = ['Medical'];
  
  // All Indian states and UT
  const allStates = [
    'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh', 'Goa', 'Gujarat', 
    'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka', 'Kerala', 'Madhya Pradesh', 
    'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab', 
    'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura', 'Uttar Pradesh', 
    'Uttarakhand', 'West Bengal', 'Andaman and Nicobar Islands', 'Chandigarh', 
    'Dadra and Nagar Haveli and Daman and Diu', 'Delhi', 'Jammu and Kashmir', 
    'Ladakh', 'Lakshadweep', 'Puducherry'
  ];

  const basePrice = 299;
  const finalPrice = basePrice - discount;

  // Scroll to top when showing payment page
  useEffect(() => {
    if (showPayment) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [showPayment]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleStateChange = (state: string) => {
    setFormData(prev => ({
      ...prev,
      states: prev.states.includes(state)
        ? prev.states.filter(s => s !== state)
        : [...prev.states, state]
    }));
  };

  const handleSelectAllStates = () => {
    setFormData(prev => ({
      ...prev,
      states: prev.states.length === allStates.length ? [] : [...allStates]
    }));
  };

  const handlePromoCodeApply = async () => {
    if (!formData.promoCode.trim()) {
      toast.error('Please enter a promo code');
      return;
    }

    const validation = await validatePromoCode(formData.promoCode, 'personalized_list');
    if (validation.isValid) {
      setDiscount(validation.discount);
      setPromoApplied(true);
      toast.success(`🎉 Promo code applied! You saved ₹${validation.discount}`);
    } else {
      toast.error('Invalid or expired promo code');
      setPromoApplied(false);
      setDiscount(0);
    }
  };

  const handlePromoCodeRemove = () => {
    setFormData(prev => ({ ...prev, promoCode: '' }));
    setPromoApplied(false);
    setDiscount(0);
    toast.success('Promo code removed');
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      toast.error('Please enter your name');
      return false;
    }
    if (!formData.email.trim()) {
      toast.error('Please enter your email');
      return false;
    }
    if (!formData.exam) {
      toast.error('Please select your exam');
      return false;
    }
    if (!formData.rank.trim()) {
      toast.error('Please enter your rank');
      return false;
    }
    if (!formData.category) {
      toast.error('Please select your category');
      return false;
    }
    if (!formData.stream) {
      toast.error('Please select your stream');
      return false;
    }
    return true;
  };

  const proceedToPayment = () => {
    if (!validateForm()) return;
    setShowPayment(true);
  };

  const testRazorpayConnection = async () => {
    setPaymentLoading(true);
    try {
      toast.loading('Testing payment system...', { id: 'debug-test' });
      const result = await debugRazorpayConnection();
      console.log('Debug Result:', result);
      
      if (result.success) {
        toast.success('Payment system test completed - check console for details', { id: 'debug-test' });
      } else {
        toast.error('Payment system test failed - check console', { id: 'debug-test' });
      }
    } catch (error) {
      console.error('Debug test failed:', error);
      toast.error('Debug test failed - check console', { id: 'debug-test' });
    } finally {
      setPaymentLoading(false);
    }
  };

  const handlePayment = async () => {
    setPaymentLoading(true);
    
    try {
      toast.loading('Initializing payment...', { id: 'payment-process' });
      
      // Load Razorpay script
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        
        toast.error('Failed to load payment gateway. Please check your internet connection.', { id: 'payment-process' });
        setPaymentLoading(false);
        return;
      }

      // Free path: promo gives 100% discount, skip Razorpay entirely
      if (finalPrice === 0) {
        try {
          await personalizedListSubmissionService.create({
            name: formData.name,
            email: formData.email,
            exam: formData.exam,
            rank: formData.rank,
            category: formData.category,
            states: formData.states,
            stream: formData.stream,
            amount_paid: 0,
            promo_code: promoApplied ? formData.promoCode : undefined,
            discount_amount: discount,
            payment_status: 'completed',
          });
        } catch (dbError: any) {
          toast.error('Failed to save your details. Please try again.', { id: 'payment-process' });
          setPaymentLoading(false);
          return;
        }
        toast.success('Promo applied! Generating your personalized college list...', { id: 'payment-process' });
        await generateCollegeList();
        return;
      }

      // Create order
      let order;
      try {
        order = await createRazorpayOrder(finalPrice);
        console.log('Order created:', order);
        toast.success('Payment order created successfully!', { id: 'payment-process' });
      } catch (orderError: any) {
        console.error('Order creation failed:', orderError);
        toast.error(`Failed to create payment order: ${orderError.message}`, { id: 'payment-process' });
        setPaymentLoading(false);
        return;
      }

      // Save submission to database first
      let submission;
      try {
        submission = await personalizedListSubmissionService.create({
          name: formData.name,
          email: formData.email,
          exam: formData.exam,
          rank: formData.rank,
          category: formData.category,
          states: formData.states,
          stream: formData.stream,
          amount_paid: finalPrice,
          promo_code: promoApplied ? formData.promoCode : undefined,
          discount_amount: discount,
          payment_status: 'pending',
          razorpay_order_id: order.id
        });
        console.log('Submission saved:', submission);
      } catch (dbError: any) {
        console.error('Database error:', dbError);
        toast.error('Failed to save your details. Please try again.', { id: 'payment-process' });
        setPaymentLoading(false);
        return;
      }

      // Initialize Razorpay payment
      const options = {
        key: RAZORPAY_KEY_ID,
        amount: order.amount,
        currency: order.currency,
        name: 'Grad Vikalp',
        description: 'Personalized College List',
        order_id: order.id,
        prefill: {
          name: formData.name,
          email: formData.email,
        },
        theme: {
          color: '#3b82f6'
        },
        handler: async (response: RazorpayResponse) => {
          try {
            console.log('Payment successful:', response);
            toast.loading('Verifying payment...', { id: 'payment-process' });
            
            // Verify payment
            const verificationResult = await verifyPayment(
              response.razorpay_order_id,
              response.razorpay_payment_id,
              response.razorpay_signature,
              submission.id
            );

            if (verificationResult.success) {
              toast.success('🎉 Payment verified! Generating your personalized college list...', { id: 'payment-process' });
              
              // Generate and show the list
              await generateCollegeList();
            } else {
              toast.error('Payment verification failed. Please contact support with Payment ID: ' + response.razorpay_payment_id, { id: 'payment-process' });
            }
            
          } catch (error: any) {
            console.error('Error in payment verification:', error);
            toast.error('Payment completed but verification failed. Payment ID: ' + response.razorpay_payment_id, { id: 'payment-process' });
          }
        },
        modal: {
          ondismiss: () => {
            toast.error('Payment cancelled', { id: 'payment-process' });
            setPaymentLoading(false);
          }
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();

    } catch (error: any) {
      console.error('Payment error:', error);
      toast.error('Failed to initiate payment. Please try again.', { id: 'payment-process' });
      setPaymentLoading(false);
    }
  };

  const generateCollegeList = async () => {
    setLoading(true);
    try {
      console.log('=== STARTING COLLEGE LIST GENERATION ===');
      console.log('Form data:', formData);
      
      const rank = parseInt(formData.rank);
      // Prepare state preferences - if no states selected, pass undefined
      const statePreferences = formData.states.length > 0 ? formData.states : undefined;
      
      console.log('Calling generateRecommendations with:', {
        exam: formData.exam,
        rank,
        category: formData.category,
        statePreferences: statePreferences,
        stream: formData.stream
      });
      
      const recommendations = await personalizedCollegeService.generateRecommendations(
        formData.exam,
        rank,
        formData.category as 'General' | 'OBC' | 'SC' | 'ST' | 'EWS',
        statePreferences,
        undefined // No course preferences
      );

      console.log('Generated recommendations:', recommendations);

      setGeneratedList(recommendations);
      setShowResults(true);
      setShowPayment(false);
      setPaymentLoading(false);
      
      if (recommendations.length > 0) {
        toast.success(`✅ Generated ${recommendations.length} personalized recommendations!`);
      } else {
        toast.error('No recommendations found for your criteria. Please try different parameters or contact support.');
      }
    } catch (error) {
      console.error('Error generating college list:', error);
      toast.error('Failed to generate college list. Please try again.');
      setPaymentLoading(false);
    } finally {
      setLoading(false);
    }
  };

  const downloadPDF = async () => {
    try {
      toast.loading('Generating PDF report...', { id: 'pdf-generation' });

      const reportData = {
        studentInfo: {
          name: formData.name,
          email: formData.email,
          exam: formData.exam,
          rank: formData.rank,
          category: formData.category,
          stream: formData.stream,
          states: formData.states.length > 0 ? formData.states.join(', ') : 'No specific preference'
        },
        recommendations: {
          colleges: generatedList,
          total: generatedList.length
        },
        paymentInfo: {
          amountPaid: finalPrice,
          promoCode: promoApplied ? formData.promoCode : null,
          discount: discount
        }
      };

      // Generate and download PDF
      const { base64, fileName } = await generatePDFBase64(reportData);

      // Trigger browser download
      const link = document.createElement('a');
      link.href = `data:application/pdf;base64,${base64}`;
      link.download = fileName;
      link.click();

      toast.success('PDF report downloaded!', { id: 'pdf-generation' });

      // Send PDF to user's email in the background
      toast.loading('Sending report to your email...', { id: 'pdf-email' });
      try {
        const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-pdf-email`;
        const res = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            to: formData.email,
            name: formData.name,
            exam: formData.exam,
            rank: formData.rank,
            category: formData.category,
            totalColleges: generatedList.length,
            pdfBase64: base64,
            fileName,
          }),
        });
        const result = await res.json();
        if (result.success) {
          toast.success(`Report also sent to ${formData.email}`, { id: 'pdf-email' });
        } else {
          toast.error('Could not email the report. Please download and save the PDF.', { id: 'pdf-email' });
        }
      } catch {
        toast.error('Could not email the report. Please download and save the PDF.', { id: 'pdf-email' });
      }
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Failed to generate PDF. Please try again.', { id: 'pdf-generation' });
    }
  };

  const getCategoryColor = (type: string) => {
    switch (type) {
      case 'Dream': return 'bg-primary';
      case 'Target': return 'bg-success';
      case 'Safety': return 'bg-warning';
      default: return 'bg-secondary';
    }
  };

  const getProbabilityColor = (probability: number) => {
    if (probability >= 80) return 'text-success';
    if (probability >= 50) return 'text-warning';
    return 'text-danger';
  };

  if (showPayment) {
    return (
      <div style={{ paddingTop: '80px' }} className="hero-gradient">
        <SEOHead
          title="Complete Your Payment - Personalized College List | Grad Vikalp"
          description="Complete your payment to get your personalized college list based on your exam rank, category, and preferences. Secure payment powered by Razorpay."
          keywords="college payment, personalized list payment, secure payment, college recommendations, JEE college list, NEET college list"
          url="/personalized-list"
          type="website"
        />
        <div className="container py-5">
          <Link
            to="/"
            className="btn btn-link text-primary text-decoration-none mb-4 p-0"
          >
            <ArrowLeft className="me-2" size={16} />
            Back to Home
          </Link>

          <div className="row justify-content-center">
            <div className="col-lg-6">
              <div className="card border-0 shadow-xl">
                <div className="bg-blue-gradient text-white p-4 text-center">
                  <CreditCard size={32} className="mb-2" />
                  <h2 className="h3 fw-bold mb-2">Complete Your Payment</h2>
                  <p className="text-blue-100 mb-0">Secure payment powered by Razorpay</p>
                </div>
                
                <div className="card-body p-4">
                  {/* Debug Section */}
                  <div className="mb-4">
                    <button
                      onClick={() => setDebugMode(!debugMode)}
                      className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-2"
                    >
                      <Bug size={14} />
                      {debugMode ? 'Hide' : 'Show'} Debug Tools
                    </button>
                    
                    {debugMode && (
                      <div className="mt-3 p-3 bg-light rounded">
                        <h6 className="fw-bold mb-2">Debug Tools</h6>
                        <div className="d-flex gap-2 flex-wrap">
                          <button
                            onClick={testRazorpayConnection}
                            disabled={paymentLoading}
                            className="btn btn-sm btn-warning"
                          >
                            Test Payment System
                          </button>
                        </div>
                        <small className="text-muted d-block mt-2">
                          Check browser console for detailed logs
                        </small>
                      </div>
                    )}
                  </div>

                  {/* Order Summary */}
                  <div className="mb-4">
                    <h4 className="fw-bold text-dark mb-3">Order Summary</h4>
                    <div className="bg-light rounded-3 p-3">
                      <div className="d-flex justify-content-between mb-2">
                        <span>Personalized College List</span>
                        <span>₹{basePrice}</span>
                      </div>
                      {promoApplied && (
                        <div className="d-flex justify-content-between mb-2 text-success">
                          <span>Discount ({formData.promoCode})</span>
                          <span>-₹{discount}</span>
                        </div>
                      )}
                      <hr />
                      <div className="d-flex justify-content-between fw-bold">
                        <span>Total Amount</span>
                        <span className="text-primary">₹{finalPrice}</span>
                      </div>
                    </div>
                  </div>

                  {/* Student Details */}
                  <div className="mb-4">
                    <h5 className="fw-bold text-dark mb-3">Student Details</h5>
                    <div className="row g-2 small">
                      <div className="col-6">
                        <strong>Name:</strong> {formData.name}
                      </div>
                      <div className="col-6">
                        <strong>Email:</strong> {formData.email}
                      </div>
                      <div className="col-6">
                        <strong>Exam:</strong> {formData.exam}
                      </div>
                      <div className="col-6">
                        <strong>Rank:</strong> {formData.rank}
                      </div>
                      <div className="col-6">
                        <strong>Category:</strong> {formData.category}
                      </div>
                      <div className="col-6">
                        <strong>Stream:</strong> {formData.stream}
                      </div>
                      {formData.states.length > 0 && (
                        <div className="col-12">
                          <strong>States:</strong> {formData.states.slice(0, 3).join(', ')}{formData.states.length > 3 && ` +${formData.states.length - 3} more`}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Promo Code Section */}
                  {!promoApplied && (
                    <div className="mb-4">
                      <h5 className="fw-bold text-dark mb-3">Have a Promo Code?</h5>
                      <div className="input-group">
                        <input
                          type="text"
                          value={formData.promoCode}
                          onChange={handleInputChange}
                          name="promoCode"
                          className="form-control"
                          placeholder="Enter promo code"
                        />
                        <button
                          onClick={handlePromoCodeApply}
                          className="btn btn-outline-primary"
                          type="button"
                        >
                          Apply
                        </button>
                      </div>
                      <small className="text-muted">
                        {/* Try: NEET2026, GETLISTNOW, RANK2LIST, WELCOME50   */}
                      </small>
                    </div>
                  )}

                  {promoApplied && (
                    <div className="mb-4">
                      <div className="alert alert-success d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center gap-2">
                          <CheckCircle size={16} />
                          <span>Promo code "{formData.promoCode}" applied!</span>
                        </div>
                        <button
                          onClick={handlePromoCodeRemove}
                          className="btn btn-sm btn-outline-danger"
                        >
                          <ThumbsDown size={14} />
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Payment Button */}
                  <div className="d-grid gap-2">
                    <button
                      onClick={handlePayment}
                      disabled={paymentLoading}
                      className="btn btn-primary btn-lg"
                    >
                      {paymentLoading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                          Processing Payment...
                        </>
                      ) : (
                        <>
                          <CreditCard size={20} className="me-2" />
                          Pay Securely ₹{finalPrice}
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => setShowPayment(false)}
                      className="btn btn-outline-secondary"
                    >
                      Back to Form
                    </button>
                  </div>

                  <div className="text-center mt-3">
                    <small className="text-muted">
                      🔒 Secure payment powered by Razorpay
                    </small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ paddingTop: '80px' }} className="hero-gradient">
      <SEOHead
        title="Get Your Personalized College List - AI-Powered Recommendations | Grad Vikalp"
        description="Get a personalized college list based on your JEE/NEET rank, category, and preferences. AI-powered recommendations with admission probability analysis for just ₹299. Instant results with detailed college information."
        keywords="personalized college list, JEE college recommendations, NEET college list, college admission probability, rank-based college list, AI college recommendations, college selection tool"
        url="/personalized-list"
        type="website"
      />
      <div className="container py-5">
        {/* Back Button */}
        <Link
          to="/"
          className="btn btn-link text-primary text-decoration-none mb-4 p-0"
        >
          <ArrowLeft className="me-2" size={16} />
          Back to Home
        </Link>

        <div className="text-center mb-5">
          <div className="d-flex align-items-center justify-content-center gap-3 mb-3">
            <Brain className="text-primary" size={32} />
            <h1 className="display-4 fw-bold text-dark mb-0">
              Get Your Personalized College List
            </h1>
          </div>
          <p className="lead text-muted">
            Get a customized list of medical colleges based on your NEET rank, category, and preferences
          </p>
        </div>

        {!showResults ? (
          <div className="row g-5">
            {/* Left side - Process Info */}
            <div className="col-lg-4">
              <div className="vstack gap-4">
                <div className="card border-0 shadow-lg">
                  <div className="card-body p-4">
                    <h3 className="h4 fw-bold text-dark mb-4">How It Works</h3>
                    
                    <div className="vstack gap-4">
                      <div className="d-flex align-items-start gap-3">
                        <div className="step-number bg-primary text-white">1</div>
                        <div>
                          <h4 className="h6 fw-semibold text-dark mb-1">Enter Details</h4>
                          <p className="text-muted small mb-0">Provide your exam rank and preferences</p>
                        </div>
                      </div>
                      
                      <div className="d-flex align-items-start gap-3">
                        <div className="step-number bg-success text-white">2</div>
                        <div>
                          <h4 className="h6 fw-semibold text-dark mb-1">Secure Payment</h4>
                          <p className="text-muted small mb-0">Pay securely via Razorpay</p>
                        </div>
                      </div>
                      
                      <div className="d-flex align-items-start gap-3">
                        <div className="step-number bg-info text-white">3</div>
                        <div>
                          <h4 className="h6 fw-semibold text-dark mb-1">Get Your List</h4>
                          <p className="text-muted small mb-0">Instant access to personalized recommendations</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="card border-0 shadow-lg">
                  <div className="card-body p-4">
                    <h3 className="h4 fw-bold text-dark mb-4">Pricing</h3>
                    <div className="text-center">
                      <div className="display-6 fw-bold text-primary">₹299</div>
                      <p className="text-muted">One-time payment</p>
                      <div className="alert alert-success">
                        <strong>Limited Time!</strong><br />
                        Use promo codes for discounts
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right side - Form */}
            <div className="col-lg-8">
              <div className="card border-0 shadow-xl">
                <div className="bg-blue-gradient text-white p-4">
                  <h2 className="h3 fw-bold mb-2">Personalized College List Form</h2>
                  <p className="text-blue-100 mb-0">Fill out your details to get customized recommendations</p>
                </div>
                
                <div className="card-body p-4">
                  <form onSubmit={(e) => { e.preventDefault(); proceedToPayment(); }}>
                    <div className="row g-3">
                      <div className="col-md-6">
                        <label className="form-label fw-semibold text-dark">Full Name *</label>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          className="form-control form-control-lg"
                          placeholder="Enter your full name"
                          required
                        />
                      </div>

                      <div className="col-md-6">
                        <label className="form-label fw-semibold text-dark">Email Address *</label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          className="form-control form-control-lg"
                          placeholder="Enter your email"
                          required
                        />
                      </div>

                      <div className="col-md-6">
                        <label className="form-label fw-semibold text-dark">Exam *</label>
                        <select
                          name="exam"
                          value={formData.exam}
                          onChange={handleInputChange}
                          className="form-select form-select-lg"
                          required
                        >
                          <option value="">Select Exam</option>
                          {exams.map(exam => (
                            <option key={exam} value={exam}>{exam}</option>
                          ))}
                        </select>
                      </div>

                      <div className="col-md-6">
                        <label className="form-label fw-semibold text-dark">Your Rank *</label>
                        <div className="input-group">
                          <span className="input-group-text">
                            <Calculator size={16} />
                          </span>
                          <input
                            type="number"
                            name="rank"
                            value={formData.rank}
                            onChange={handleInputChange}
                            className="form-control form-control-lg"
                            placeholder="Enter your rank"
                            required
                          />
                        </div>
                      </div>

                      <div className="col-md-6">
                        <label className="form-label fw-semibold text-dark">Category *</label>
                        <select
                          name="category"
                          value={formData.category}
                          onChange={handleInputChange}
                          className="form-select form-select-lg"
                          required
                        >
                          <option value="">Select Category</option>
                          {categories.map(category => (
                            <option key={category} value={category}>{category}</option>
                          ))}
                        </select>
                      </div>

                      <div className="col-md-6">
                        <label className="form-label fw-semibold text-dark">Preferred Stream *</label>
                        <div className="input-group">
                          <span className="input-group-text">
                            <GraduationCap size={16} />
                          </span>
                          <select
                            name="stream"
                            value={formData.stream}
                            onChange={handleInputChange}
                            className="form-select form-select-lg"
                            required
                          >
                            <option value="">Select Stream</option>
                            {streams.map(stream => (
                              <option key={stream} value={stream}>{stream}</option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="col-12">
                        <label className="form-label fw-semibold text-dark">State Preferences (Select multiple)</label>
                        <div className="mb-2">
                          <button
                            type="button"
                            onClick={handleSelectAllStates}
                            className="btn btn-sm btn-outline-primary"
                          >
                            {formData.states.length === allStates.length ? 'Deselect All' : 'Select All States'}
                          </button>
                          <small className="text-muted ms-2">
                            ({formData.states.length} selected)
                          </small>
                        </div>
                        <div 
                          className="border rounded p-3" 
                          style={{ 
                            maxHeight: '200px', 
                            overflowY: 'auto',
                            background: 'linear-gradient(to bottom, rgba(255,255,255,0.9), rgba(248,249,250,0.9))',
                            border: '2px solid #e9ecef !important'
                          }}
                        >
                          <div className="row g-2">
                            {allStates.map(state => (
                              <div key={state} className="col-md-4 col-6">
                                <div className="form-check">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id={`state-${state}`}
                                    checked={formData.states.includes(state)}
                                    onChange={() => handleStateChange(state)}
                                  />
                                  <label className="form-check-label small" htmlFor={`state-${state}`}>
                                    {state}
                                  </label>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                        <small className="text-muted">Scroll to see more states. Leave empty for all states</small>
                      </div>

                      <div className="col-12">
                        <button
                          type="submit"
                          disabled={loading}
                          className="btn btn-primary w-100 d-flex align-items-center justify-content-center gap-2"
                        >
                          {loading ? (
                            <>
                              <span className="spinner-border spinner-border-sm" role="status"></span>
                              Submitting...
                            </>
                          ) : (
                            <>
                              <CreditCard size={16} />
                              Proceed to Payment (₹299)
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* Results Section */
          <div className="card border-0 shadow-xl">
            <div className="bg-success text-white p-4">
              <h2 className="h3 fw-bold mb-2">Your Personalized College Recommendations</h2>
              <p className="text-light mb-0">Based on your {formData.exam} rank of {formData.rank} ({formData.category} category) for {formData.stream}</p>
            </div>
            
            <div className="card-body p-4">
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h3 className="h4 fw-bold text-dark">Recommended Colleges ({generatedList.length})</h3>
                <div className="d-flex gap-2">
                  <button
                    onClick={downloadPDF}
                    className="btn btn-success d-flex align-items-center gap-2"
                  >
                    <Download size={16} />
                    Download & Receive via Email
                  </button>
                  <button
                    onClick={() => setShowResults(false)}
                    className="btn btn-outline-primary"
                  >
                    Generate New List
                  </button>
                </div>
              </div>

              {/* Summary Stats */}
              <div className="row g-3 mb-4">
                <div className="col-md-12">
                  <div className="card bg-primary bg-opacity-10 border-0">
                    <div className="card-body text-center p-3">
                      <h5 className="fw-bold text-primary mb-1">
                        {generatedList.length}
                      </h5>
                      <small className="text-muted">Total Recommendations</small>
                    </div>
                  </div>
                </div>
              </div>

              <div className="row g-4">
                {generatedList.map((recommendation, index) => (
                  <div key={`${recommendation.college.id}-${recommendation.id}`} className="col-lg-6">
                    <div className="card border h-100">
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-start mb-3">
                          <div>
                            <h5 className="fw-bold text-dark mb-1">{recommendation.college.name}</h5>
                            <h6 className="text-primary mb-1">{recommendation.branch_name}</h6>
                          </div>
                          <div className="d-flex gap-2">
                          </div>
                        </div>
                        
                        <div className="vstack gap-2 small">
                          <div className="d-flex justify-content-between">
                            <span className="text-muted">Location:</span>
                            <span className="fw-medium">{recommendation.college.location}, {recommendation.college.state}</span>
                          </div>
                          <div className="d-flex justify-content-between">
                            <span className="text-muted">College Type:</span>
                            <span className="fw-medium">{recommendation.college.college_type.replace('_', ' ')}</span>
                          </div>
                          <div className="d-flex justify-content-between">
                            <span className="text-muted">Duration:</span>
                            <span className="fw-medium">{recommendation.duration}</span>
                          </div>
                          <div className="d-flex justify-content-between">
                            <span className="text-muted">NIRF Ranking:</span>
                            <span className="fw-medium">#{recommendation.college.nirf_ranking}</span>
                          </div>
                          <div className="d-flex justify-content-between">
                            <span className="text-muted">Fees:</span>
                            <span className="fw-medium">{recommendation.college.fees_annual}</span>
                          </div>
                          <div className="d-flex justify-content-between">
                            <span className="text-muted">Seats Available:</span>
                            <span className="fw-medium">
                              {recommendation.branch_name === 'MBBS' ? recommendation.seats_available : 
                               recommendation.college.seats_total}
                            </span>
                          </div>
                          <div className="d-flex justify-content-between">
                            <span className="text-muted">Expected Cutoff ({formData.category}):</span>
                            <span className="fw-medium">{recommendation[`cutoff_${formData.category.toLowerCase()}` as keyof BranchRecommendation] || 'N/A'}</span>
                          </div> 
                          {recommendation.best_round && (
                            <div className="d-flex justify-content-between">
                              <span className="text-muted">Best Round:</span>
                              <span className="fw-medium text-success">
                                {recommendation.best_round.round_name} ({recommendation.best_round.year}) - Cutoff: {recommendation.best_round[`cutoff_${formData.category.toLowerCase()}` as keyof typeof recommendation.best_round]}
                              </span>
                            </div>
                          )}
                          <div className="d-flex justify-content-between">
                            <span className="text-muted">Placement:</span>
                            <span className="fw-medium">{recommendation.college.placement_percentage}% • {recommendation.college.average_package}</span>
                          </div>
                        </div>

                        {/* Counseling Rounds Section */}
                        {recommendation.all_rounds && recommendation.all_rounds.length > 0 && (
                          <div className="mt-3 pt-3 border-top">
                            <div className="d-flex align-items-center gap-2 mb-2">
                              <BarChart3 size={16} className="text-primary" />
                              <span className="fw-semibold text-primary">Counseling Rounds</span>
                            </div>
                            <div className="table-responsive">
                              <table className="table table-sm table-bordered">
                                <thead className="table-light">
                                  <tr>
                                    <th>Round</th>
                                    <th>Expected Cutoff ({formData.category})</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {recommendation.all_rounds.map((round) => (
                                    <tr key={round.id}>
                                      <td>{round.round_name}</td>
                                      <td>{round[`cutoff_${formData.category.toLowerCase()}` as keyof typeof round] || 'N/A'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}

                        <div className="mt-3 pt-3 border-top">
                          <a 
                            href={recommendation.college.website} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="btn btn-outline-primary btn-sm w-100"
                          >
                            Visit Website
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {generatedList.length === 0 && (
                <div className="text-center py-5">
                  <GraduationCap size={64} className="text-muted mb-3" />
                  <h4 className="text-muted">No recommendations found</h4>
                  <p className="text-muted">
                    Try adjusting your preferences or check if your rank is within the available range for your preferred stream.
                  </p>
                  <div className="alert alert-info">
                    <strong>Debug Info:</strong> Check the browser console for detailed logs about the recommendation process.
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* SEO Content Section */}
        <section className="py-5 bg-white">
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-lg-10">
                <div className="mb-5">
                  <h2 className="h2 fw-bold text-dark mb-4">Personalized College List: Find Your Perfect College Match</h2>
                  <p className="text-muted">
                    Confused about which college you can get into with your JEE or NEET rank? Our Personalized College List service takes the guesswork out. Just enter your rank, category and preferences—and our AI-powered recommendation engine will give you a list of colleges where you have the highest chances of admission.
Whether you're preparing for JEE Main, JEE Advanced, or NEET, we analyze previous year counseling data points to bring you a customized college list that fits your goals.

                  </p>
                </div>

                <div className="row g-4 mb-5">
                  <div className="col-md-6">
                    <div className="card h-100 border-0 shadow-sm">
                      <div className="card-body p-4">
                        <h3 className="h4 fw-bold text-dark mb-3">How Our Recommendation Engine Works</h3>
                        <p className="text-muted mb-4">
                          Our advanced algorithm takes into account multiple factors to generate personalized college recommendations:
                        </p>
                        <ul className="text-muted">
                          <li className="mb-2">Your entrance exam rank (JEE Main, JEE Advanced or NEET)</li>
                          <li className="mb-2">Reservation category (General, OBC, SC, ST, EWS)</li>
                          <li className="mb-2">Historical cutoff trends across multiple counseling rounds</li>
                          <li className="mb-2">State preferences and location preferences</li>
                          <li className="mb-2">College type preferences (IIT, NIT, Government, Private)</li>
                          <li className="mb-2">Branch and specialization preferences</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="card h-100 border-0 shadow-sm">
                      <div className="card-body p-4">
                        <h3 className="h4 fw-bold text-dark mb-3">What You will Get</h3>
                        <p className="text-muted mb-4">
                          Our personalized college list provides comprehensive information to help you make informed decisions:
                        </p>
                        <ul className="text-muted">
                          <li className="mb-2"> A clear breakdown of Dream, Target, and Safe colleges based on your rank
</li>
                          <li className="mb-2">Admission chances (in %) for each college to help you understand where you stand
</li>
                          <li className="mb-2">In depth college details including fees, NIRF ranking, and placement records
</li>
                          <li className="mb-2">Branch-wise cutoff data matched to your category and rank
</li>
                          <li className="mb-2"> Insights from past counseling rounds to see how cutoffs have changed over time
</li>
                          <li className="mb-2"> A PDF report you can download and refer to anytime
</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mb-5">
                  <h3 className="h3 fw-bold text-dark mb-4">Why Choose Our Personalized College List?</h3>
                  <div className="row g-4">
                    <div className="col-md-4">
                      <div className="card h-100 border-0 shadow-sm">
                        <div className="card-body p-4 text-center">
                          <div className="rounded-circle bg-primary bg-opacity-10 p-3 d-inline-flex mb-3">
                            <Brain className="text-primary" size={32} />
                          </div>
                          <h4 className="h5 fw-bold text-dark mb-3">AI Powered Accuracy</h4>
                          <p className="text-muted">
                            Based on your profile, our recommendation engine analyses  data using advanced algorithms to deliver precise college recommendations.
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="card h-100 border-0 shadow-sm">
                        <div className="card-body p-4 text-center">
                          <div className="rounded-circle bg-success bg-opacity-10 p-3 d-inline-flex mb-3">
                            <BarChart3 className="text-success" size={32} />
                          </div>
                          <h4 className="h5 fw-bold text-dark mb-3">Data Driven Insights</h4>
                          <p className="text-muted">
                           To give precise cutoff trends and admission probability, we use actual historical data from prior counselling sessions.
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="card h-100 border-0 shadow-sm">
                        <div className="card-body p-4 text-center">
                          <div className="rounded-circle bg-info bg-opacity-10 p-3 d-inline-flex mb-3">
                            <Download className="text-info" size={32} />
                          </div>
                          <h4 className="h5 fw-bold text-dark mb-3">Instant Results</h4>
                          <p className="text-muted">
                            Get your personalized college list instantly after payment, with a downloadable PDF report for future reference.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mb-5">
                  <h3 className="h3 fw-bold text-dark mb-4">Frequently Asked Questions</h3>
                  <div className="accordion" id="faqAccordion">
                    <div className="accordion-item">
                      <h2 className="accordion-header">
                        <button className="accordion-button" type="button" data-bs-toggle="collapse" data-bs-target="#faq1">
                          How accurate are the college recommendations?
                        </button>
                      </h2>
                      <div id="faq1" className="accordion-collapse collapse show" data-bs-parent="#faqAccordion">
                        <div className="accordion-body">
                          Our predictions are based on historical data and real trends. While admission cutoffs can change, we give a probability percentage for each college so you can plan smartly.
                        </div>
                      </div>
                    </div>
                    <div className="accordion-item">
                      <h2 className="accordion-header">
                        <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#faq2">
                          Can I get a refund if I am not satisfied?
                        </button>
                      </h2>
                      <div id="faq2" className="accordion-collapse collapse" data-bs-parent="#faqAccordion">
                        <div className="accordion-body">
                          Please refer to our refund policy for details. Generally, once the personalized list is generated and delivered, refunds are not provided as you have received the service. However, if there are technical issues preventing list generation, please contact our support team.
                        </div>
                      </div>
                    </div>
                    <div className="accordion-item">
                      <h2 className="accordion-header">
                        <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#faq3">
                          How many colleges will be included in my personalized list?
                        </button>
                      </h2>
                      <div id="faq3" className="accordion-collapse collapse" data-bs-parent="#faqAccordion">
                        <div className="accordion-body">
                          The number of colleges varies based on your rank, category, and preferences, but typically includes 50 colleges categorized as Dream, Target, and Safety options. Each college includes detailed branch information and historical cutoff data.
                        </div>
                      </div>
                    </div>
                    <div className="accordion-item">
                      <h2 className="accordion-header">
                        <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#faq4">
                          Can I generate multiple lists with different preferences?
                        </button>
                      </h2>
                      <div id="faq4" className="accordion-collapse collapse" data-bs-parent="#faqAccordion">
                        <div className="accordion-body">
                          Each payment generates one personalized list. If you want to explore different scenarios (different ranks, categories, or preferences), you would need to purchase additional lists. We offer promo codes for returning customers.
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="h3 fw-bold text-dark mb-4">Ready to Find Your Dream College?</h3>
                  <p className="text-muted mb-4">
                    Don't leave your college selection to chance. Get data driven, personalized recommendations based on your rank and preferences. Our AI powered system analyzes thousands of data points to help you make the best decision for your future.
                  </p>
                  <div className="text-center">
                    <a href="#top" className="btn btn-primary btn-lg px-5">
                      Get Your Personalized List Now
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default PersonalizedList;